import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Chip,
  Avatar,
  Button,
  Divider
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import CommentIcon from '@mui/icons-material/Comment';
import EditIcon from '@mui/icons-material/Edit';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import ForumIcon from '@mui/icons-material/Forum';
import { Opinion } from '../../interface/Opinion';
import { getUserOpinions, getUserVotedOpinions, getUserCommentedOpinions, voteOpinion } from '../../services/OpinionService';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import OpinionCard from '../dashboard/OpinionCard';
import MainLayout from '../layout/MainLayout';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`my-opinions-tabpanel-${index}`}
      aria-labelledby={`my-opinions-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const MyOpinions: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [myPosts, setMyPosts] = useState<Opinion[]>([]);
  const [myReactions, setMyReactions] = useState<(Opinion & { userVote?: 'agree' | 'disagree' | null })[]>([]);
  const [myComments, setMyComments] = useState<(Opinion & { userCommentCount?: number; lastCommentDate?: Date | null })[]>([]);
  const [loading, setLoading] = useState(true);
  const [voting, setVoting] = useState<string | null>(null);

  useEffect(() => {
    if (user?._id) {
      loadAllData();
    }
  }, [user]);

  const loadAllData = async () => {
    if (!user?._id) return;
    
    try {
      setLoading(true);
      const [posts, reactions, comments] = await Promise.all([
        getUserOpinions(user._id),
        getUserVotedOpinions(user._id),
        getUserCommentedOpinions(user._id)
      ]);
      
      setMyPosts(posts.opinions);
      setMyReactions(reactions.opinions);
      setMyComments(comments.opinions);
    } catch (error) {
      console.error('Error loading user activity:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (opinionId: string, voteType: 'agree' | 'disagree') => {
    if (!user?._id) return;

    try {
      setVoting(opinionId);
      const response = await voteOpinion(opinionId, {
        userId: user._id,
        voteType
      });

      // Update the opinion in the appropriate list
      if (activeTab === 1) {
        setMyReactions(prev => prev.map(opinion =>
          opinion._id === opinionId
            ? { ...opinion, ...response.opinion, userVote: response.userVote }
            : opinion
        ));
      }
    } catch (error) {
      console.error('Error voting:', error);
    } finally {
      setVoting(null);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return (
      <MainLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress size={60} />
        </Box>
      </MainLayout>
    );
  }

  return (
    <MainLayout title="My Opinions">
      <Container maxWidth="lg" sx={{ py: { xs: 2, md: 3 }, px: { xs: 1.5, sm: 2, md: 3 } }}>
        <Card>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.9375rem',
                  minHeight: 64
                }
              }}
            >
              <Tab
                icon={<EditIcon />}
                iconPosition="start"
                label={
                  <Box>
                    <Typography variant="body1" fontWeight={500}>
                      My Posts
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {myPosts.length} opinions
                    </Typography>
                  </Box>
                }
              />
              <Tab
                icon={<HowToVoteIcon />}
                iconPosition="start"
                label={
                  <Box>
                    <Typography variant="body1" fontWeight={500}>
                      My Reactions
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {myReactions.length} votes
                    </Typography>
                  </Box>
                }
              />
              <Tab
                icon={<ForumIcon />}
                iconPosition="start"
                label={
                  <Box>
                    <Typography variant="body1" fontWeight={500}>
                      My Comments
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {myComments.length} discussions
                    </Typography>
                  </Box>
                }
              />
            </Tabs>
          </Box>

          <CardContent sx={{ p: 3 }}>
            {/* My Posts Tab */}
            <TabPanel value={activeTab} index={0}>
              {myPosts.length === 0 ? (
                <Box textAlign="center" py={6}>
                  <EditIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: '1.125rem', mb: 1 }}>
                    No posts yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontSize: '0.9375rem' }}>
                    Start sharing your thoughts with the community
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => navigate('/create')}
                    startIcon={<EditIcon />}
                  >
                    Create Your First Opinion
                  </Button>
                </Box>
              ) : (
                <Stack spacing={2}>
                  {myPosts.map((opinion) => (
                    <OpinionCard
                      key={opinion._id}
                      opinion={opinion}
                      currentUserId={user?._id}
                      onVote={handleVote}
                      voting={voting === opinion._id}
                    />
                  ))}
                </Stack>
              )}
            </TabPanel>

            {/* My Reactions Tab */}
            <TabPanel value={activeTab} index={1}>
              {myReactions.length === 0 ? (
                <Box textAlign="center" py={6}>
                  <HowToVoteIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: '1.125rem', mb: 1 }}>
                    No reactions yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontSize: '0.9375rem' }}>
                    Start voting on opinions to see them here
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => navigate('/home')}
                    startIcon={<ThumbUpIcon />}
                  >
                    Explore Opinions
                  </Button>
                </Box>
              ) : (
                <Stack spacing={2}>
                  {myReactions.map((opinion) => (
                    <Card key={opinion._id} sx={{ position: 'relative' }}>
                      <CardContent sx={{ p: 2.5 }}>
                        {/* Vote Badge */}
                        <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
                          <Chip
                            icon={opinion.userVote === 'agree' ? <ThumbUpIcon /> : <ThumbDownIcon />}
                            label={opinion.userVote === 'agree' ? 'Agreed' : 'Disagreed'}
                            color={opinion.userVote === 'agree' ? 'success' : 'error'}
                            size="small"
                            sx={{ fontSize: '0.8125rem', height: 24 }}
                          />
                        </Box>

                        <Box display="flex" alignItems="center" mb={2}>
                          <Avatar
                            sx={{
                              width: 44,
                              height: 44,
                              bgcolor: 'primary.main',
                              mr: 1.5,
                              fontSize: '1.125rem'
                            }}
                          >
                            {opinion.username.charAt(0).toUpperCase()}
                          </Avatar>
                          <Box flex={1}>
                            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                              <Typography variant="body1" fontWeight={500} sx={{ fontSize: '0.9375rem' }}>
                                {opinion.username}
                              </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                              {formatDistanceToNow(new Date(opinion.createdAt), { addSuffix: true })}
                            </Typography>
                          </Box>
                        </Box>

                        <Typography
                          variant="body1"
                          sx={{
                            lineHeight: 1.6,
                            mb: 2.5,
                            color: 'text.primary',
                            fontSize: '0.9375rem',
                            pr: 8
                          }}
                        >
                          {opinion.content}
                        </Typography>

                        <Divider sx={{ my: 1.5 }} />

                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box display="flex" alignItems="center" gap={2}>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <ThumbUpIcon fontSize="small" color="success" />
                              <Typography variant="body2" sx={{ fontSize: '0.9375rem' }}>
                                {opinion.agreeCount || 0}
                              </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <ThumbDownIcon fontSize="small" color="error" />
                              <Typography variant="body2" sx={{ fontSize: '0.9375rem' }}>
                                {opinion.disagreeCount || 0}
                              </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <CommentIcon fontSize="small" color="action" />
                              <Typography variant="body2" sx={{ fontSize: '0.9375rem' }}>
                                {opinion.commentCount || 0}
                              </Typography>
                            </Box>
                          </Box>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => navigate(`/opinions/${opinion._id}`)}
                          >
                            View Discussion
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              )}
            </TabPanel>

            {/* My Comments Tab */}
            <TabPanel value={activeTab} index={2}>
              {myComments.length === 0 ? (
                <Box textAlign="center" py={6}>
                  <ForumIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: '1.125rem', mb: 1 }}>
                    No comments yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontSize: '0.9375rem' }}>
                    Join discussions by commenting on opinions
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => navigate('/home')}
                    startIcon={<CommentIcon />}
                  >
                    Explore Opinions
                  </Button>
                </Box>
              ) : (
                <Stack spacing={2}>
                  {myComments.map((opinion) => (
                    <Card key={opinion._id}>
                      <CardContent sx={{ p: 2.5 }}>
                        <Box display="flex" alignItems="center" mb={2}>
                          <Avatar
                            sx={{
                              width: 44,
                              height: 44,
                              bgcolor: 'primary.main',
                              mr: 1.5,
                              fontSize: '1.125rem'
                            }}
                          >
                            {opinion.username.charAt(0).toUpperCase()}
                          </Avatar>
                          <Box flex={1}>
                            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                              <Typography variant="body1" fontWeight={500} sx={{ fontSize: '0.9375rem' }}>
                                {opinion.username}
                              </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                              {formatDistanceToNow(new Date(opinion.createdAt), { addSuffix: true })}
                            </Typography>
                          </Box>
                          <Chip
                            icon={<CommentIcon />}
                            label={`${opinion.userCommentCount || 0} ${opinion.userCommentCount === 1 ? 'comment' : 'comments'}`}
                            color="primary"
                            size="small"
                            sx={{ fontSize: '0.8125rem', height: 24 }}
                          />
                        </Box>

                        <Typography
                          variant="body1"
                          sx={{
                            lineHeight: 1.6,
                            mb: 2.5,
                            color: 'text.primary',
                            fontSize: '0.9375rem'
                          }}
                        >
                          {opinion.content}
                        </Typography>

                        <Divider sx={{ my: 1.5 }} />

                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box display="flex" alignItems="center" gap={2}>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <ThumbUpIcon fontSize="small" color="success" />
                              <Typography variant="body2" sx={{ fontSize: '0.9375rem' }}>
                                {opinion.agreeCount || 0}
                              </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <ThumbDownIcon fontSize="small" color="error" />
                              <Typography variant="body2" sx={{ fontSize: '0.9375rem' }}>
                                {opinion.disagreeCount || 0}
                              </Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <CommentIcon fontSize="small" color="action" />
                              <Typography variant="body2" sx={{ fontSize: '0.9375rem' }}>
                                {opinion.commentCount || 0}
                              </Typography>
                            </Box>
                          </Box>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => navigate(`/opinions/${opinion._id}`)}
                            startIcon={<CommentIcon />}
                          >
                            View Discussion
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              )}
            </TabPanel>
          </CardContent>
        </Card>
      </Container>
    </MainLayout>
  );
};

export default MyOpinions;
